import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import {
  Reveal,
  CountUp,
  ImageReveal,
  RevealText,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import { Button } from "@project/components/ui/button";
import {
  HandHeart,
  Stethoscope,
  UtensilsCrossed,
  Sparkles,
  ShieldCheck,
  Heart,
  Quote,
  ArrowRight,
  CheckCircle2,
  Activity,
} from "lucide-react";
import { useI18n } from "@project/lib/use-i18n";
import careImg from "@project/assets/home-care.webp";
import mealImg from "@project/assets/home-meal.webp";
import medicalImg from "@project/assets/home-medical.webp";
import { galleryCategoryService } from "@project/services/admin/gallery-category.service";
import { galleryService } from "@project/services/admin/gallery.service";
import { resolveMediaUrl } from "@project/lib/utils";

const mainServices = [
  {
    icon: Heart,
    titleKey: "care.elderCare.services.shelter.title",
    bodyKey: "care.elderCare.services.shelter.body",
  },
  {
    icon: UtensilsCrossed,
    titleKey: "care.elderCare.services.bhojan.title",
    bodyKey: "care.elderCare.services.bhojan.body",
  },
  {
    icon: Stethoscope,
    titleKey: "care.elderCare.services.care.title",
    bodyKey: "care.elderCare.services.care.body",
  },
  {
    icon: Sparkles,
    titleKey: "care.elderCare.services.dignity.title",
    bodyKey: "care.elderCare.services.dignity.body",
  },
];

const facilities = [
  "care.elderCare.facilities.items.capacity",
  "care.elderCare.facilities.items.clinic",
  "care.elderCare.facilities.items.physio",
  "care.elderCare.facilities.items.library",
  "care.elderCare.facilities.items.access",
  "care.elderCare.facilities.items.garden",
  "care.elderCare.facilities.items.entertainment",
];

const stories = [
  {
    nameKey: "care.elderCare.stories.items.kantilal.name",
    yearsKey: "care.elderCare.stories.items.kantilal.years",
    quoteKey: "care.elderCare.stories.items.kantilal.quote",
  },
  {
    nameKey: "care.elderCare.stories.items.saroj.name",
    yearsKey: "care.elderCare.stories.items.saroj.years",
    quoteKey: "care.elderCare.stories.items.saroj.quote",
  },
  {
    nameKey: "care.elderCare.stories.items.hasmukh.name",
    yearsKey: "care.elderCare.stories.items.hasmukh.years",
    quoteKey: "care.elderCare.stories.items.hasmukh.quote",
  },
];

export default function ElderCareProgramPage() {
  const { t } = useI18n();
  const [dynamicGallery, setDynamicGallery] = useState<{ src: string; title: string; desc?: string }[]>([]);

  useEffect(() => {
    let ignore = false;
    const loadCareGallery = async () => {
      try {
        const catRes = await galleryCategoryService.listPage({ page: 1, page_size: 50 });
        const categories = (catRes.data || []).filter(c => c.active !== false);
        const targetCat = categories.find(c =>
          c.category_name?.toLowerCase().includes("elder") ||
          c.category_name?.toLowerCase().includes("care") ||
          c.category_name?.toLowerCase().includes("resident") ||
          c.category_name?.toLowerCase().includes("outdoor")
        ) || categories[0];

        if (targetCat) {
          const itemRes = await galleryService.listPage({
            category: targetCat.id,
            page: 1,
            page_size: 10,
          });
          const activeItems = (itemRes.data || []).filter(i => i.active !== false).slice(0, 3);
          if (!ignore && activeItems.length > 0) {
            const mapped = activeItems
              .map(item => ({
                src: resolveMediaUrl(item.image_url || item.image) || "",
                title: item.title || targetCat.category_name || "Elder Care",
                desc: item.description || "",
              }))
              .filter(i => !!i.src);
            if (mapped.length > 0) {
              setDynamicGallery(mapped);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load dynamic elder care gallery", err);
      }
    };
    loadCareGallery();
    return () => {
      ignore = true;
    };
  }, []);

  const staticCareGallery = [
    {
      src: careImg,
      label: t("care.elderCare.gallery.items.care"),
      desc: "Compassionate 24/7 care, comfort, and dignified shelter for elderly residents.",
    },
    {
      src: mealImg,
      label: t("care.elderCare.gallery.items.meals"),
      desc: "Pure, wholesome, and nutritious satvik meals tailored for elderly well-being.",
    },
    {
      src: medicalImg,
      label: t("care.elderCare.gallery.items.health"),
      desc: "Regular medical checkups, physiotherapy, and complete healthcare support.",
    },
  ];

  return (
    <PageShell>
      <PageHero
        eyebrow={t("care.elderCare.hero.eyebrow")}
        title={t("care.elderCare.hero.title")}
        subtitle={t("care.elderCare.hero.subtitle")}
        image={careImg}
      />

      {/* Impact strip */}
      <section className="border-border/60 bg-card/40 border-b">
        <Stagger className="mx-auto grid max-w-site gap-4 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              n: 298,
              suffix: "+",
              label: t("care.elderCare.impact.residentsCared"),
            },
            {
              n: 24,
              suffix: " Yrs",
              label: t("care.elderCare.impact.lifetimeCare"),
            },
            {
              n: 149,
              suffix: "+",
              label: t("care.elderCare.impact.lastRites"),
            },
            {
              n: 100,
              suffix: "%",
              label: t("care.elderCare.impact.donorFunded"),
            },
          ].map((s, i) => (
            <StaggerItem key={s.label} index={i} from="zoom">
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm">
                <div className="bg-primary/10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-primary">
                  <HandHeart className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-serif text-3xl font-semibold text-foreground">
                    <CountUp to={s.n} suffix={s.suffix} />
                  </div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Care services */}
      <section className="mx-auto max-w-site px-4 py-16">
        <Reveal variant="morph" className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            {t("care.elderCare.overview.eyebrow")}
          </p>
          <h2 className="mt-3 font-sans text-3xl font-bold text-foreground md:text-4xl">
            <RevealText delay={120}>
              {t("care.elderCare.overview.heading")}
            </RevealText>
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            {t("care.elderCare.overview.description")}
          </p>
        </Reveal>
        <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {mainServices.map((s, i) => (
            <StaggerItem key={s.titleKey} index={i}>
              <article className="card-lift h-full rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="bg-primary/10 grid h-12 w-12 place-items-center rounded-xl text-primary">
                  <s.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 !font-sans !text-2xl !font-medium text-foreground">
                  {t(s.titleKey)}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t(s.bodyKey)}
                </p>
              </article>
            </StaggerItem>
          ))}
        </Stagger>

        {/* Featured Landscape Card for Paralysis Rehabilitation */}
        <Reveal variant="morph" delay={150} className="mt-5">
          <article className="card-lift rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
              <div className="bg-primary/10 grid h-14 w-14 shrink-0 place-items-center rounded-2xl text-primary">
                <Activity className="h-7 w-7" />
              </div>
              <div className="space-y-1">
                <h3 className="!font-sans !text-2xl !font-medium text-foreground sm:text-2xl">
                  {t("care.elderCare.services.physio.title")}
                </h3>
                <p className="text-sm text-muted-foreground sm:text-base">
                  {t("care.elderCare.services.physio.body")}
                </p>
              </div>
            </div>
          </article>
        </Reveal>
      </section>

      {/* Facilities */}
      <section className="bg-secondary/40">
        <div className="mx-auto grid max-w-site gap-10 px-4 py-16 md:grid-cols-2 md:items-center">
          <figure className="overflow-hidden rounded-3xl border border-border shadow-sm">
            <ImageReveal direction="left">
              <img
                src={mealImg}
                alt={t("care.elderCare.facilities.imgAlt")}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </ImageReveal>
          </figure>
          <Reveal variant="right" delay={150}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
              {t("care.elderCare.facilities.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">
              {t("care.elderCare.facilities.heading")}
            </h2>
            <p className="mt-3 text-muted-foreground">
              {t("care.elderCare.facilities.description")}
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {facilities.map(f => (
                <li
                  key={f}
                  className="text-foreground/80 flex items-start gap-2 text-sm"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{" "}
                  {t(f)}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Impact stories */}
      <section className="mx-auto max-w-site px-4 py-16">
        <Reveal variant="fade" className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            {t("care.elderCare.stories.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">
            {t("care.elderCare.stories.heading")}
          </h2>
        </Reveal>
        <Stagger className="mt-12 grid gap-5 md:grid-cols-3">
          {stories.map((s, i) => (
            <StaggerItem
              key={s.nameKey}
              index={i}
              from={i === 0 ? "left" : i === 2 ? "right" : "up"}
            >
              <figure className="card-lift h-full rounded-3xl border border-border bg-card p-7 shadow-sm">
                <Quote className="h-6 w-6 text-gold" />
                <blockquote className="mt-3 font-serif text-lg italic leading-snug text-foreground">
                  {t(s.quoteKey)}
                </blockquote>
                <figcaption className="mt-6 border-t border-border pt-4">
                  <div className="text-sm font-semibold text-foreground">
                    {t(s.nameKey)}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t(s.yearsKey)}
                  </div>
                </figcaption>
              </figure>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Photo gallery */}
      <section className="mx-auto max-w-site px-4 py-16">
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
            {t("care.elderCare.gallery.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
            {t("care.elderCare.gallery.heading")}
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">
            A look into the daily warmth, personalized medical care, and family-like living at Jivandhara.
          </p>
        </Reveal>
        <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(dynamicGallery.length > 0
            ? dynamicGallery.map(g => ({ label: g.title, src: g.src, desc: g.desc }))
            : staticCareGallery
          ).slice(0, 3).map((g, i) => (
            <StaggerItem key={g.label + i} index={i} from="zoom">
              <figure className="card-lift group relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <ImageReveal direction="up" delay={i * 120}>
                  <img
                    src={g.src}
                    alt={g.label}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </ImageReveal>
                <div className="from-primary/90 via-primary/30 pointer-events-none absolute inset-0 bg-gradient-to-t to-transparent opacity-80 transition-opacity group-hover:opacity-95" />
                <figcaption className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <h3 className="font-serif text-lg font-semibold">{g.label}</h3>
                  {g.desc && <p className="mt-1 text-xs text-white/80">{g.desc}</p>}
                </figcaption>
              </figure>
            </StaggerItem>
          ))}
        </Stagger>
        <div className="mt-8 text-center">
          <Button
            asChild
            variant="outline"
            className="border-primary/30 btn-lift rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <Link to="/gallery">
              {t("care.elderCare.gallery.viewFull")}{" "}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center">
          <Reveal variant="morph">
            <ShieldCheck className="mx-auto h-7 w-7 text-gold" />
            <p className="mt-4 font-serif !text-3xl font-bold tracking-wide text-gold sm:text-3xl">
              {t("care.elderCare.cta.tagline")}
            </p>
            <h2 className="mt-2 font-serif !text-2xl font-semibold md:text-4xl text-primary-foreground">
              {t("care.elderCare.cta.heading")}
            </h2>
            <p className="text-primary-foreground/80 mx-auto mt-3 max-w-xl">
              {t("care.elderCare.cta.description")}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button
                asChild
                size="lg"
                className="hover:bg-gold/90 btn-lift rounded-full bg-gold text-gold-foreground hover:shadow-lg"
              >
                <Link to="/donate">
                  <HandHeart className="mr-2 h-4 w-4" />{" "}
                  {t("care.elderCare.cta.support")}
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-primary-foreground/40 hover:bg-primary-foreground/10 btn-lift rounded-full bg-transparent text-primary-foreground hover:text-primary-foreground"
              >
                <Link to="/contact">{t("care.elderCare.cta.visit")}</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
